import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Logo } from "@/components/Logo";
import FooterContact from "@/components/FooterContact";
import FloatingThemeToggle from "@/components/FloatingThemeToggle";
import SEO from "@/components/SEO";

export default function Personvern() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="legal-personvern-page">
      <SEO
        title="Personvernerklæring — ClientFlow"
        description="Slik håndterer ClientFlow personopplysninger og kundedata. GDPR-vennlig norsk bookingsystem."
        path="/personvern"
      />
      <FloatingThemeToggle />
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" data-testid="legal-back-home">
            <Logo className="h-7 w-auto" />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
            data-testid="legal-back-link"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.75} />
            Tilbake
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">
          Sist oppdatert · 9. februar 2026
        </p>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-3">
          Personvernerklæring
        </h1>
        <p className="text-base text-muted-foreground mb-10 leading-relaxed">
          ClientFlow er i en lukket demo- og pilotfase. Vi tar personvern på alvor og samler bare inn det som trengs for at tjenesten skal fungere.
        </p>

        <section className="space-y-8 text-[15px] leading-relaxed">
          <Block title="1. Hvem vi er">
            ClientFlow drives foreløpig som en lukket pilot. Du kan kontakte oss på <Mail /> for spørsmål om personvern, innsyn eller sletting.
          </Block>

          <Block title="2. Hvilke data vi behandler">
            <p className="mb-3">For deg som bruker (bedriftseier eller ansatt):</p>
            <ul className="list-disc pl-5 space-y-1 mb-5">
              <li>navn og e-postadresse (innlogging)</li>
              <li>passord (lagres kryptert / hashet)</li>
              <li>profilbilde, hvis du laster det opp</li>
              <li>bedriftsdetaljer du selv legger inn (logo, adresse, åpningstider)</li>
            </ul>
            <p className="mb-3">For dine kunder (lagt inn av deg eller via offentlig bookingside):</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>navn</li>
              <li>telefonnummer</li>
              <li>e-postadresse</li>
              <li>bookingdata (tidspunkt, valgt tjeneste, status)</li>
              <li>oppfølgingsnotater og utkast til SMS/e-post</li>
            </ul>
          </Block>

          <Block title="3. Hvorfor vi behandler dataen">
            Dataen brukes til å levere bookingtjenesten, vise kalender og kundekartotek, generere oppfølgingsmeldinger og holde kontakt med deg som kunde av ClientFlow. Vi bruker den ikke til markedsføring mot dine kunder.
          </Block>

          <Block title="4. Behandlingsansvar">
            Du som bedriftseier er behandlingsansvarlig for dine kunders data. ClientFlow er databehandler og behandler dataen på dine vegne i henhold til vilkårene.
          </Block>

          <Block title="5. Lagring og sikkerhet">
            Data lagres i en MongoDB-database hos vår skyleverandør. Vi tilstreber lagring innenfor EU/EØS. Tilgang er begrenset til personell som trenger den for drift og support. Passord er hashet (bcrypt).
          </Block>

          <Block title="6. Deling med tredjeparter">
            Vi selger aldri data. For at enkelte funksjoner skal fungere sender vi nødvendige felter til:
            <ul className="list-disc pl-5 space-y-1 mt-3">
              <li><strong>AI-tekstgenerering:</strong> tekstinnhold du selv ber om å få generert (f.eks. utkast til oppfølging) sendes til vår AI-leverandør for å lage forslaget.</li>
              <li><strong>E-post (bookingbekreftelse, påminnelse 24 timer før, endre/avbestill-lenke):</strong> mottakers e-post sendes til Resend (vår e-postleverandør) kun ved utsending. SMS er foreløpig mocket — faktiske SMS sendes ikke ut i pilotfasen.</li>
            </ul>
          </Block>

          <Block title="7. Lagringstid">
            Data lagres så lenge kontoen din er aktiv. Du kan be om sletting når som helst. Etter at en konto avsluttes sletter vi dataen innen rimelig tid, med mindre vi er pålagt å beholde den (f.eks. regnskapsplikt).
          </Block>

          <Block title="8. Dine rettigheter">
            Du har rett til innsyn, retting og sletting av personopplysninger om deg. Send forespørsel til <Mail />. Du har også rett til å klage til Datatilsynet.
          </Block>

          <Block title="9. Cookies">
            ClientFlow bruker kun nødvendige cookies for innlogging (sesjonstoken). Vi bruker ikke sporings-cookies for reklame.
          </Block>

          <Block title="10. Endringer">
            Vi kan oppdatere denne erklæringen mens produktet utvikles. Datoen øverst viser når den sist ble endret.
          </Block>

          <Block title="11. Kontakt">
            Har du spørsmål om hvordan vi håndterer dataen din? Send oss en e-post på <Mail />.
          </Block>
        </section>
      </main>

      <footer className="border-t border-border py-8 mt-6">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-muted-foreground">
          <span>© 2026 ClientFlow</span>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <Link to="/vilkar" className="hover:text-foreground">Vilkår</Link>
            <Link to="/personvern" className="hover:text-foreground">Personvern</Link>
            <FooterContact />
          </div>
        </div>
      </footer>
    </div>
  );
}

function Block({ title, children }) {
  return (
    <div>
      <h2 className="text-lg font-semibold mb-2">{title}</h2>
      <div className="text-muted-foreground">{children}</div>
    </div>
  );
}

function Mail() {
  return (
    <a
      href="mailto:hei@clientflow.no"
      className="text-foreground underline underline-offset-4 hover:no-underline"
      data-testid="legal-contact-email"
    >
      hei@clientflow.no
    </a>
  );
}
